import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Paragraph } from '@/api/types/stowed-element'
import { DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { parseStoredLeading } from '@/lib/text/leading'

import { LEADING_MAX, LEADING_METRIC_SENTINEL, LEADING_MIN } from '../constants/typography-ranges'
import {
  computeMetricLeadingPt,
  computeNarrowLeadingPt,
  computeWideLeadingPt,
  matchLeadingChip,
  toStoredLeadingString,
} from '@/lib/text/leading-presets'
import { paragraphsLeading } from '../helpers/run-properties'
import {
  resolveSpacingCommit,
  spacingSliderValueText,
  validateSpacingInput,
} from '../helpers/spacing-input'
import { useSliderCommitGuard } from './use-slider-commit-guard'

type LeadingChip = 'Narrow' | 'Metric' | 'Wide'

const LEADING_INPUT_RULES = {
  min: LEADING_MIN,
  max: LEADING_MAX,
  rangeMessage: `Value: ${LEADING_MIN}–${LEADING_MAX}`,
  strict: false,
}

interface LeadingFieldOptions {
  disabled: boolean
  hasTextSelectionTarget: boolean
  defaultFontSize: number | undefined
  fontSizeValue: number
  emitStyleUpdate: (update: TextRunStyleUpdate) => void
  restoreActiveEditor: () => void
}

/** Leading: Narrow/Metric/Wide chips, slider and input, plus the resync guard that keeps a
 *  just-committed (or just-reverted) value from being stomped by the next resync. */
export function useLeadingField({
  disabled,
  hasTextSelectionTarget,
  defaultFontSize,
  fontSizeValue,
  emitStyleUpdate,
  restoreActiveEditor,
}: LeadingFieldOptions) {
  // Leading state — "Metric" means font-metric-driven mode (no explicit pt value)
  const [leadingChip, setLeadingChip] = useState<LeadingChip | null>('Metric')
  // The applied leading in pt for every mode, Metric included, so the input and slider always read
  // one value. Metric used to leave this unset and the slider recomputed `fontSizeValue × 1.2`
  // from the panel's font-size state, which could differ from the point size the input was synced
  // with — 57.6 in the input against 16.8 on the slider (AC10, WMPCM-9899).
  const [leadingPt, setLeadingPt] = useState<number>(() =>
    computeMetricLeadingPt(defaultFontSize ?? DEFAULT_POINT_SIZE)
  )
  const [leadingInputValue, setLeadingInputValue] = useState<string>(() => String(leadingPt))
  // The size Metric/Narrow/Wide are computed against: the largest size on the selected lines. Not
  // the Font Size field's value, which is 14 for a mixed selection.
  const [leadingSizePt, setLeadingSizePt] = useState<number>(
    () => defaultFontSize ?? DEFAULT_POINT_SIZE
  )
  const [leadingMixed, setLeadingMixed] = useState(false)
  const [leadingError, setLeadingError] = useState(false)
  const [leadingErrorMsg, setLeadingErrorMsg] = useState<string>('')
  const leadingInputFocused = useRef(false)
  /**
   * Set right after a successful (non-error) Leading input commit-on-blur; consumed by the very
   * next resync attempt, which it skips once. `restoreActiveEditor()` at the end of that same blur
   * handler restores focus/selection into the live text-edit session, firing `selectionchange` and
   * bumping `editSelectionTick` — which re-runs the selection-sync effect BEFORE the style
   * update just committed has necessarily settled at the (possibly now-collapsed) restored caret
   * position, so that first resync can read back a stale/absent leading and stomp the input back to
   * a freshly-computed Metric default. Every blur ends in a valid, populated field (a commit or a
   * revert), so this one-shot skip covers all of them.
   */
  const suppressNextLeadingResyncRef = useRef(false)
  /** The `editingShapeTextId` observed on the previous run of the selection-sync effect (`null`
   *  when not editing) — used solely to detect when the editing session's identity changes, so
   *  `suppressNextLeadingResyncRef` can be invalidated across that change. See its own comment. */
  const prevEditingShapeTextIdForLeadingRef = useRef<string | null>(null)

  // The skip above is only for the refocus at the end of that blur. When the refocus fired no
  // `selectionchange`, it skipped the user's next selection instead and the panel kept the previous
  // line's value, so typing it again applied nothing. A click or key press is always the user's.
  useEffect(() => {
    const cancelSuppression = () => {
      suppressNextLeadingResyncRef.current = false
    }

    document.addEventListener('pointerdown', cancelSuppression, true)
    document.addEventListener('keydown', cancelSuppression, true)

    return () => {
      document.removeEventListener('pointerdown', cancelSuppression, true)
      document.removeEventListener('keydown', cancelSuppression, true)
    }
  }, [])

  const leadingControlsDisabled = disabled || !hasTextSelectionTarget

  // Interprets a stored leading string and syncs all leading state.
  // Absent / blank / "Metric" / legacy ratio (≤5, no explicit-absolute marker) → Metric mode.
  // Absolute pt value (>5, or explicitly marked at any magnitude — see `parseStoredLeading`) →
  // numeric; chip highlights Narrow/Wide when it matches that preset for `fontSizePt`, otherwise
  // no chip is active (a genuine custom value).
  const syncLeadingFromStoredValue = useCallback(
    (stored: string | undefined, mixed: boolean, fontSizePt: number) => {
      setLeadingMixed(mixed)
      setLeadingSizePt(fontSizePt)
      setLeadingError(false)
      setLeadingErrorMsg('')

      if (mixed) {
        setLeadingChip(null)
        setLeadingInputValue('')
        return
      }

      // Delegates to the SAME parser the rest of the app reads a stored leading with — a hand-
      // rolled `parseFloat` + magnitude check here used to disagree with it on exactly the values
      // this panel itself now writes (`"0pt"`, `"2pt"`): `parseFloat("0pt")` is `0`, which the old
      // check folded into "legacy ratio, treat as Metric" on every resync, silently snapping the
      // chip/slider/input back to the Metric display right after a custom sub-6pt value was set.
      const parsed = parseStoredLeading(stored)

      if (!parsed || parsed.isRatio) {
        // Metric/absent, or a legacy ratio with no explicit-absolute marker → Metric.
        const metricPt = computeMetricLeadingPt(fontSizePt)

        setLeadingChip('Metric')
        setLeadingPt(metricPt)
        setLeadingInputValue(String(metricPt))
        return
      }

      setLeadingPt(parsed.n)
      setLeadingInputValue(String(parsed.n))
      setLeadingChip(matchLeadingChip(parsed.n, fontSizePt))
    },
    // setState functions are stable references — empty deps is intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleChipChange = (val: string) => {
    if (leadingControlsDisabled) return
    if (!val) return

    setLeadingError(false)
    setLeadingErrorMsg('')
    setLeadingMixed(false)

    if (val === LEADING_METRIC_SENTINEL) {
      const metricPt = computeMetricLeadingPt(leadingSizePt)

      setLeadingChip('Metric')
      setLeadingPt(metricPt)
      setLeadingInputValue(String(metricPt))
      emitStyleUpdate({ leading: LEADING_METRIC_SENTINEL })
    } else {
      const pt =
        val === 'Narrow'
          ? computeNarrowLeadingPt(leadingSizePt)
          : computeWideLeadingPt(leadingSizePt)

      setLeadingChip(val as 'Narrow' | 'Wide')
      setLeadingPt(pt)
      setLeadingInputValue(String(pt))
      emitStyleUpdate({ leading: String(pt) })
    }

    restoreActiveEditor()
  }

  const sliderGuard = useSliderCommitGuard(leadingInputFocused, restoreActiveEditor)

  const sliderValue = [hasTextSelectionTarget ? leadingPt : computeMetricLeadingPt(fontSizeValue)]

  const sliderValueText = spacingSliderValueText(sliderValue[0], 'points', {
    mixed: leadingMixed,
    unavailable: leadingControlsDisabled,
  })

  const handleSliderChange = (val: number[]) => {
    if (leadingControlsDisabled) return

    // Same guard the Leading input itself uses on focus (and Kerning/Tracking's
    // own sliders use) — without it, a keyboard step's `onValueCommit`
    // restores canvas focus and triggers a resync before this value has actually
    // been applied, clobbering it with a stale read.
    leadingInputFocused.current = true

    const pt = val[0]

    setLeadingChip(matchLeadingChip(pt, leadingSizePt))
    setLeadingPt(pt)
    setLeadingInputValue(String(pt))
    setLeadingMixed(false)
    setLeadingError(false)
    setLeadingErrorMsg('')
    // `toStoredLeadingString` marks a value at/below LEADING_RATIO_BAND_MAX
    // explicit (`helpers/leading-presets.ts`) so it reads back as the exact absolute pt
    // length dragged here, rather than as a ratio multiplier — the thumb, this
    // value, and what's applied all agree, all the way down to 0.
    emitStyleUpdate({ leading: toStoredLeadingString(pt) })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (leadingControlsDisabled) return

    const raw = e.target.value

    setLeadingInputValue(raw)

    // Validation only — nothing is applied until Enter/blur (`handleInputBlur`). Applying each
    // keystroke made "4" (on the way to "45") its own applied value and undo step, so one undo
    // after committing 45 landed on 4 instead of the previous value (AC18, WMPCM-9899).
    const result = validateSpacingInput(raw, LEADING_INPUT_RULES)

    if (result.kind === 'valid') {
      setLeadingError(false)
      setLeadingErrorMsg('')
      return
    }

    setLeadingError(true)
    setLeadingErrorMsg('message' in result ? result.message : '')
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || e.ctrlKey || e.metaKey || e.altKey) return

    // Commit-or-revert on Enter the same way blur does (Kerning/Tracking do the same).
    e.preventDefault()
    ;(e.target as HTMLInputElement).blur()
  }

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    leadingInputFocused.current = true
    e.target.select()
  }

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    leadingInputFocused.current = false

    const committed = resolveSpacingCommit(e.target.value, LEADING_INPUT_RULES)

    // `leadingPt` is the value applied to the text right now, in every mode.
    if (committed !== null && committed !== leadingPt) {
      setLeadingChip(matchLeadingChip(committed, leadingSizePt))
      setLeadingPt(committed)
      setLeadingInputValue(String(committed))
      setLeadingMixed(false)
      // `toStoredLeadingString` marks a value at/below LEADING_RATIO_BAND_MAX explicit instead of
      // clamping it out of that band — it reads back as exactly `committed`, not a ratio
      // multiplier, so the applied leading matches what was typed down to a literal 0.
      emitStyleUpdate({ leading: toStoredLeadingString(committed) })
    } else {
      // Blank, invalid, out of range or unchanged: restore what is applied (a mixed selection
      // goes back to its "Mixed" placeholder). Also strips a trailing "19." to "19".
      setLeadingInputValue(leadingMixed ? '' : String(leadingPt))
    }

    // The field is valid again either way, so nothing is left to flag (AC14).
    setLeadingError(false)
    setLeadingErrorMsg('')
    suppressNextLeadingResyncRef.current = true

    restoreActiveEditor()
  }

  /**
   * Invalidates a pending one-shot Leading resync suppression (see `suppressNextLeadingResyncRef`)
   * the moment the editing session's identity actually changes — e.g. the user switched to a
   * different object (or exited/re-entered live text-edit) between the blur that armed it and
   * this run. Otherwise the flag can survive that switch unconsumed (the Select-Tool branch
   * never touches it) and wrongly suppress the FIRST legitimate resync after the ORIGINAL shape
   * is reselected, leaving the Leading display stuck on whatever the intervening shape showed.
   */
  const trackEditingSession = useCallback((editingShapeTextId: string | null) => {
    if (editingShapeTextId !== prevEditingShapeTextIdForLeadingRef.current) {
      suppressNextLeadingResyncRef.current = false
    }

    prevEditingShapeTextIdForLeadingRef.current = editingShapeTextId
  }, [])

  /** Live text editing: resync from the editor's selection style, unless guarded. */
  const resyncFromLiveEdit = useCallback(
    (style: TextRunStyleUpdate | null | undefined, defaultSize: number | undefined) => {
      if (leadingInputFocused.current) return

      if (suppressNextLeadingResyncRef.current) {
        // Consume the one-shot suppression set by a just-succeeded Leading blur commit — see
        // `suppressNextLeadingResyncRef`'s doc comment. Skip exactly this resync; the next one
        // (a genuine caret move, selection change, etc.) runs normally.
        suppressNextLeadingResyncRef.current = false
      } else {
        syncLeadingFromStoredValue(
          style?.leading,
          style?.mixedLeading ?? false,
          // An empty frame has no run to read a size from. Fall back to the size the frame will
          // actually type at (the book default the Font Size control already shows), not the
          // 14pt `DEFAULT_POINT_SIZE` — that made a fresh 8pt frame read Leading 16.8 until the
          // first keystroke (WMPCM-11367). The selected lines' largest size comes first.
          style?.leadingPointSize ??
            (typeof style?.pointSize === 'number'
              ? style.pointSize
              : (defaultSize ?? DEFAULT_POINT_SIZE))
        )
      }
    },
    [syncLeadingFromStoredValue]
  )

  /** Select-Tool whole-box selection: read per line, at each line's largest point size. */
  const resyncFromParagraphs = useCallback(
    (paragraphs: Paragraph[]) => {
      if (leadingInputFocused.current) return

      const summary = paragraphsLeading(paragraphs)

      syncLeadingFromStoredValue(
        summary?.leading,
        summary?.mixed ?? false,
        summary?.pointSize ?? DEFAULT_POINT_SIZE
      )
    },
    [syncLeadingFromStoredValue]
  )

  /** Nothing selected — Leading has no meaningful value to show, same as Kerning/Tracking: no
   *  chip active, input cleared so the '-' placeholder shows. */
  const clearLeading = useCallback(() => {
    setLeadingChip(null)
    setLeadingInputValue('')
    setLeadingMixed(false)
    setLeadingError(false)
    setLeadingErrorMsg('')
  }, [])

  return {
    leadingChip,
    leadingPt,
    setLeadingPt,
    leadingSizePt,
    setLeadingSizePt,
    leadingInputValue,
    setLeadingInputValue,
    leadingMixed,
    leadingError,
    leadingErrorMsg,
    leadingControlsDisabled,
    sliderValue,
    sliderValueText,
    sliderGuard,
    handleChipChange,
    handleSliderChange,
    handleInputChange,
    handleInputKeyDown,
    handleInputFocus,
    handleInputBlur,
    syncLeadingFromStoredValue,
    trackEditingSession,
    resyncFromLiveEdit,
    resyncFromParagraphs,
    clearLeading,
  }
}

export type LeadingField = ReturnType<typeof useLeadingField>
